import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Pause, Play, RotateCcw, Mic, ArrowRight, Lock, Wifi } from 'lucide-react';
import { toast } from 'sonner';
import { DeckProvider, EditAllowed, useChannel, useDeck } from '@/pres/store';
import { Stage } from '@/pres/Stage';
import { SlideRenderer } from '@/pres/SlideRenderer';
import { SLIDES, TAGS, ROLE_LABEL, TOTAL_BUDGET, fmt, level, nameOf, speakerOf } from '@/pres/slides';
import { errMsg } from '@/pres/api';

interface TimerState { running: boolean; total: number; per: Record<string, number>; last: number }
const EMPTY: TimerState = { running: false, total: 0, per: {}, last: 0 };
const TONE = { ok: 'text-emerald-400', warn: 'text-orange-400', over: 'text-red-500', none: 'text-white/40' } as const;
const BAR = { ok: 'bg-emerald-500', warn: 'bg-orange-500', over: 'bg-red-600', none: 'bg-white/20' } as const;

function useTimer(slug: string, sid: string) {
  const key = `deck:${slug}:timer`;
  const [st, setSt] = useState<TimerState>(() => { try { return JSON.parse(localStorage.getItem(key) || '') || EMPTY; } catch { return EMPTY; } });
  const sidRef = useRef(sid);
  sidRef.current = sid;
  const persist = (n: TimerState) => { localStorage.setItem(key, JSON.stringify(n)); return n; };
  useEffect(() => {
    const id = setInterval(() => setSt((s) => {
      if (!s.running) return s;
      const now = Date.now();
      const dt = (now - s.last) / 1000;
      return persist({ ...s, total: s.total + dt, per: { ...s.per, [sidRef.current]: (s.per[sidRef.current] || 0) + dt }, last: now });
    }), 250);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return {
    ...st,
    toggle: () => setSt((s) => persist({ ...s, running: !s.running, last: Date.now() })),
    reset: () => setSt(persist(EMPTY)),
  };
}

export default function Presenter() {
  const { slug } = useParams();
  return <DeckProvider slug={slug!}><PresenterInner /></DeckProvider>;
}

function Notes({ sid }: { sid: string }) {
  const { get, set, canEdit, unlock, author } = useDeck();
  const remote: string = get(`notes:${sid}`) || '';
  const [val, setVal] = useState(remote);
  const [focus, setFocus] = useState(false);
  const [code, setCode] = useState('');
  useEffect(() => { if (!focus) setVal(remote); }, [remote, focus, sid]);
  if (!canEdit) return (
    <form className="flex flex-1 flex-col items-start justify-center gap-3 rounded-2xl border border-white/10 p-5" onSubmit={async (e) => { e.preventDefault(); try { await unlock(code, author || 'Présentateur'); } catch (err) { toast.error(errMsg(err)); } }}>
      <p className="flex items-center gap-2 font-ui text-sm text-white/60"><Lock size={14} /> Les notes privées sont visibles avec le code de groupe.</p>
      <div className="flex w-full gap-2">
        <input data-testid="presenter-code-input" type="password" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Code de groupe" className="flex-1 rounded-lg border border-white/15 bg-white/5 px-3 py-2 font-ui text-sm text-white outline-none focus:border-blue-500" />
        <button data-testid="presenter-unlock-button" className="rounded-full bg-blue-600 px-4 font-app text-sm font-semibold text-white">OK</button>
      </div>
    </form>
  );
  return (
    <textarea
      data-testid="presenter-notes"
      value={val}
      onFocus={() => setFocus(true)}
      onBlur={() => setFocus(false)}
      onKeyDown={(e) => e.stopPropagation()}
      onChange={(e) => { setVal(e.target.value); set(`notes:${sid}`, e.target.value); }}
      placeholder="Notes privées pour cette slide (jamais projetées)…"
      className="min-h-0 flex-1 resize-none rounded-2xl border border-white/10 bg-white/[0.04] p-4 font-ui text-[17px] leading-relaxed text-white outline-none placeholder:text-white/25 focus:border-blue-500 transition-colors"
    />
  );
}

function PresenterInner() {
  const { slug, team, get, loaded } = useDeck();
  const [i, setI] = useState(0);
  const post = useChannel(slug, (m) => { if (m.t === 'goto') setI(m.i); });
  useEffect(() => { post({ t: 'hello' }); }, [post]);
  const d = SLIDES[i];
  const timer = useTimer(slug, d.id);
  const go = (n: number) => { const c = Math.min(SLIDES.length - 1, Math.max(0, n)); setI(c); post({ t: 'goto', i: c }); };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (['INPUT', 'TEXTAREA'].includes(t.tagName)) return;
      if (['ArrowRight', 'PageDown', ' '].includes(e.key)) { e.preventDefault(); go(i + 1); }
      else if (['ArrowLeft', 'PageUp'].includes(e.key)) { e.preventDefault(); go(i - 1); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  useEffect(() => { document.title = 'Présentateur — Plateformes numériques'; }, []);
  if (!loaded) return <div className="h-screen bg-[#0c0c0e]" />;

  const speaker = speakerOf(d.role, team);
  const next = SLIDES.slice(i + 1).find((s) => { const sp = speakerOf(s.role, team); return sp !== null && sp !== speaker; });
  const nextSp = next ? speakerOf(next.role, team) : null;
  const spent = timer.per[d.id] || 0;
  const lv = level(spent, d.budget);
  const glv = level(timer.total, TOTAL_BUDGET);
  const tags: string[] = get(`${d.id}:tags`) || [];

  return (
    <div className="flex h-screen flex-col gap-4 overflow-hidden bg-[#0c0c0e] p-5 font-ui text-white" data-testid="presenter-view">
      <header className="flex items-center gap-6">
        <div className="font-app text-sm font-semibold uppercase tracking-[0.2em] text-white/50">Mode présentateur</div>
        <span className="flex items-center gap-1.5 text-xs text-white/40"><Wifi size={13} /> Synchro locale avec la fenêtre projetée</span>
        <div className="ml-auto flex items-center gap-4">
          <div className="text-right">
            <div className={`font-app text-4xl font-bold tabular-nums ${TONE[glv]}`} data-testid="presenter-global-timer">{fmt(timer.total)}</div>
            <div className="text-xs text-white/40">sur {fmt(TOTAL_BUDGET)} · reste {fmt(TOTAL_BUDGET - timer.total)}</div>
          </div>
          <button data-testid="presenter-timer-toggle" onClick={timer.toggle} className={`flex h-12 w-12 items-center justify-center rounded-full ${timer.running ? 'bg-white text-black' : 'bg-blue-600 text-white'} active:scale-95 transition-transform`}>{timer.running ? <Pause size={20} /> : <Play size={20} />}</button>
          <button data-testid="presenter-timer-reset" onClick={timer.reset} className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"><RotateCcw size={18} /></button>
        </div>
      </header>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10"><div className={`h-full ${BAR[glv]} transition-[width] duration-300`} style={{ width: `${Math.min(100, (timer.total / TOTAL_BUDGET) * 100)}%` }} /></div>

      <div className="grid min-h-0 flex-1 grid-cols-[1.45fr_1fr] gap-5">
        <div className="flex min-h-0 flex-col gap-3">
          <div className="aspect-video w-full overflow-hidden rounded-2xl border border-white/10 bg-black" data-testid="presenter-current-preview">
            <EditAllowed.Provider value={false}><Stage><SlideRenderer key={d.id} d={d} /></Stage></EditAllowed.Provider>
          </div>
          <div className="flex items-center gap-4 rounded-2xl border border-white/10 p-4">
            <button data-testid="presenter-prev-btn" onClick={() => go(i - 1)} className="rounded-full bg-white/10 p-2 hover:bg-white/20"><ChevronLeft /></button>
            <div className="flex-1">
              <div className="flex items-baseline gap-3"><span className="font-app text-lg font-bold">{d.n}. {d.label}</span>
                {tags.map((t) => <span key={t} className="rounded-full bg-blue-600/20 px-2 text-xs text-blue-300">{TAGS.find((x) => x.id === t)?.label}</span>)}
              </div>
              {d.budget > 0 ? (
                <>
                  <div className="mt-2 h-2 overflow-hidden rounded-full bg-white/10"><div className={`h-full ${BAR[lv]} transition-[width] duration-300`} style={{ width: `${Math.min(100, (spent / d.budget) * 100)}%` }} /></div>
                  <div className={`mt-1 text-sm tabular-nums ${TONE[lv]}`} data-testid="presenter-slide-budget">{fmt(spent)} / {fmt(d.budget)} {lv === 'over' ? '· dépassement' : lv === 'warn' ? '· bientôt la fin' : ''}</div>
                </>
              ) : <div className="mt-1 text-sm text-white/40">Pas de budget pour cette slide</div>}
            </div>
            <button data-testid="presenter-next-btn" onClick={() => go(i + 1)} className="rounded-full bg-blue-600 p-2 hover:bg-blue-500"><ChevronRight /></button>
          </div>
        </div>

        <div className="flex min-h-0 flex-col gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl bg-white p-4 text-black" data-testid="presenter-current-speaker">
              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-black/50"><Mic size={13} /> Qui parle</div>
              <div className="mt-1 font-app text-3xl font-bold">{speaker === null ? 'Transition' : nameOf(speaker, team)}</div>
              <div className="text-sm text-black/50">{d.role ? ROLE_LABEL[d.role] : 'Pas d’orateur dédié'}</div>
            </div>
            <div className="rounded-2xl border border-white/15 p-4" data-testid="presenter-next-speaker">
              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-white/50"><ArrowRight size={13} /> Juste après</div>
              <div className="mt-1 font-app text-3xl font-bold">{next ? nameOf(nextSp, team) : '—'}</div>
              <div className="text-sm text-white/50">{next ? `Slide ${next.n} · ${next.label}` : 'Fin de la présentation'}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="aspect-video w-[220px] shrink-0 overflow-hidden rounded-xl border border-white/10 bg-black opacity-80" data-testid="presenter-next-preview">
              {SLIDES[i + 1] && <EditAllowed.Provider value={false}><Stage><SlideRenderer key={SLIDES[i + 1].id} d={SLIDES[i + 1]} /></Stage></EditAllowed.Provider>}
            </div>
            <div className="text-sm text-white/50">Slide suivante<br /><span className="font-app text-base text-white">{SLIDES[i + 1]?.label || '—'}</span></div>
          </div>
          <Notes sid={d.id} />
        </div>
      </div>

      <div className="flex gap-1.5 overflow-x-auto" data-testid="presenter-timeline">
        {SLIDES.map((s, j) => {
          const sp = timer.per[s.id] || 0;
          const l = level(sp, s.budget);
          return (
            <button key={s.id} onClick={() => go(j)} data-testid={`presenter-timeline-${s.n}`} className={`min-w-[78px] flex-1 rounded-lg border px-2 py-1.5 text-left transition-colors ${j === i ? 'border-blue-500 bg-blue-600/15' : 'border-white/10 hover:bg-white/5'}`}>
              <div className="text-[11px] text-white/50">{s.n} · {s.part || s.label.split(' ')[0]}</div>
              <div className={`text-xs tabular-nums ${TONE[l]}`}>{s.budget ? `${fmt(sp)}/${fmt(s.budget)}` : '—'}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
